/**
 * Authentication Page
 * Split layout matching the landing page's playful style: a calm indigo panel
 * with floating mascots on the left, the auth card on the right.
 * Supports: Login, Register, Forgot Username, Forgot Password, Reset Password
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import LoginForm from '../../components/auth/LoginForm';
import RegisterForm from '../../components/auth/RegisterForm';
import Logo from '../../components/shared/Logo';
import Mascot from '../../components/shared/Mascot';
import { forgotUsername, forgotPassword, resetPassword } from '../../api/auth';

type AuthView = 'login' | 'register' | 'forgot-username' | 'forgot-password' | 'reset-password';

const PANEL_POINTS = [
  { mascot: 'bulb' as const, chip: '#fb923c', text: 'An AI tutor that knows every lecture' },
  { mascot: 'book' as const, chip: '#fbbf24', text: "Quizzes across all six Bloom's levels" },
  { mascot: 'star' as const, chip: '#a5b4fc', text: 'Insights that find the hard parts early' },
];

const inputClass =
  'w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors';

const primaryButtonClass =
  'w-full rounded-full bg-primary-600 py-2.5 font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50';

const AuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [authView, setAuthView] = useState<AuthView>('login');

  // Forgot username state
  const [forgotUsernameEmail, setForgotUsernameEmail] = useState('');
  const [forgotUsernameResult, setForgotUsernameResult] = useState<string | null>(null);
  const [forgotUsernameError, setForgotUsernameError] = useState<string | null>(null);
  const [forgotUsernameLoading, setForgotUsernameLoading] = useState(false);

  // Forgot password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [forgotPasswordSent, setForgotPasswordSent] = useState<string | null>(null);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Reset link from email: /reset-password?token=...
  useEffect(() => {
    if (location.pathname === '/reset-password') {
      const token = new URLSearchParams(location.search).get('token');
      if (token) {
        setResetToken(token);
        setAuthView('reset-password');
      }
    }
  }, [location.pathname, location.search]);

  // Set active tab based on URL
  useEffect(() => {
    if (location.pathname === '/register') {
      setActiveTab('register');
      setAuthView('register');
    } else if (location.pathname !== '/reset-password') {
      setActiveTab('login');
      if (authView === 'register') setAuthView('login');
    }
  }, [location.pathname]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      let redirectPath = '/dashboard/student';
      if (user.role === 'admin') redirectPath = '/dashboard/admin';
      else if (user.role === 'teacher') redirectPath = '/dashboard/teacher';
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleTabChange = useCallback((tab: 'login' | 'register') => {
    setActiveTab(tab);
    setAuthView(tab);
    navigate(tab === 'login' ? '/login' : '/register', { replace: true });
  }, [navigate]);

  const backToLogin = () => {
    setAuthView('login');
    setActiveTab('login');
    setForgotUsernameEmail('');
    setForgotUsernameResult(null);
    setForgotUsernameError(null);
    setForgotPasswordEmail('');
    setForgotPasswordSent(null);
    setResetToken(null);
    setForgotPasswordError(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
    setResetSuccess(false);
  };

  // Forgot username handler
  const handleForgotUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotUsernameLoading(true);
    setForgotUsernameError(null);
    setForgotUsernameResult(null);
    try {
      const message = await forgotUsername(forgotUsernameEmail);
      setForgotUsernameResult(message);
    } catch (err: any) {
      setForgotUsernameError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setForgotUsernameLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError(null);
    try {
      const message = await forgotPassword(forgotPasswordEmail);
      setForgotPasswordSent(message);
    } catch (err: any) {
      setForgotPasswordError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Reset password handler
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    setResetError(null);
    try {
      await resetPassword(resetToken!, newPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // Determine title/subtitle based on view
  const getViewTitle = () => {
    switch (authView) {
      case 'login': return 'Welcome back';
      case 'register': return 'Create your account';
      case 'forgot-username': return 'Find your username';
      case 'forgot-password': return 'Reset your password';
      case 'reset-password': return resetSuccess ? 'Password reset!' : 'Set new password';
    }
  };

  const getViewSubtitle = () => {
    switch (authView) {
      case 'login': return 'Sign in to access your courses and lectures.';
      case 'register': return 'Join classrooms and start learning smarter.';
      case 'forgot-username': return "Enter the email you used to register and we'll show your username.";
      case 'forgot-password': return "Enter your email and we'll help you reset your password.";
      case 'reset-password': return resetSuccess ? 'You can now sign in with your new password.' : 'Enter your new password below.';
    }
  };

  const showTabs = authView === 'login' || authView === 'register';

  return (
    <div className="relative flex min-h-screen bg-white dark:bg-secondary-900">
      {/* Top bar: logo home link + theme toggle */}
      <div className="absolute left-6 top-5 z-20">
        <Logo size="md" onClick={() => navigate('/')} />
      </div>
      <button
        onClick={toggleTheme}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="absolute right-6 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-secondary-800"
      >
        {isDark ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
        )}
      </button>

      {/* Left panel — calm indigo, floating mascots, product promise */}
      <div className="relative hidden overflow-hidden bg-primary-50 p-12 dark:bg-secondary-800 lg:flex lg:w-1/2 lg:items-center lg:justify-center">
        {/* Corner mascots */}
        <div className="absolute right-14 top-16 animate-float-medium">
          <Mascot name="clock" chip="#fde047" className="h-16 w-16 shadow-medium" blinkDelay={1.2} />
        </div>
        <div className="absolute bottom-16 left-12 animate-float-fast">
          <Mascot name="apple" chip="#f472b6" className="h-14 w-14 shadow-medium" blinkDelay={2.4} />
        </div>
        <div className="absolute bottom-24 right-20 animate-float-slow">
          <Mascot name="folder" chip="#93c5fd" className="h-12 w-12 shadow-medium" blinkDelay={0.6} />
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-extrabold leading-[1.15] tracking-tight text-gray-900 dark:text-white">
            Help is{' '}
            <span className="inline-block rounded-2xl bg-primary-200/70 px-3 py-1 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300">
              always
            </span>{' '}
            in the room
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Your lectures, an AI tutor, and quizzes that grow with you, all in one classroom.
          </p>

          <div className="mt-10 flex flex-col gap-4">
            {PANEL_POINTS.map((p, i) => (
              <div
                key={p.text}
                className="flex items-center gap-4 rounded-2xl bg-white px-4 py-3 shadow-soft dark:bg-secondary-700"
              >
                <Mascot name={p.mascot} chip={p.chip} className="h-11 w-11 flex-shrink-0" blinkDelay={i * 1.1} />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{p.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — auth form */}
      <div className="flex w-full items-center justify-center px-6 py-24 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* Welcome text */}
          <div className="mb-8 text-center lg:text-left">
            <h2 key={authView + '-title'} className="animate-fade-in-up text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              {getViewTitle()}
            </h2>
            <p key={authView + '-subtitle'} className="animate-fade-in-up mt-2 text-gray-500 dark:text-gray-400" style={{ animationDelay: '0.1s' }}>
              {getViewSubtitle()}
            </p>
          </div>

          {/* Auth Card */}
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
            {/* Tabs — segmented pill, only show for login/register */}
            {showTabs && (
              <div className="mb-8 inline-flex w-full rounded-full bg-gray-100 p-1 dark:bg-secondary-700">
                <button
                  onClick={() => handleTabChange('login')}
                  className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all duration-300 ${
                    activeTab === 'login'
                      ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-600 dark:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleTabChange('register')}
                  className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all duration-300 ${
                    activeTab === 'register'
                      ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-600 dark:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Form Content */}
            <div key={authView} className="animate-fade-in">
              {/* Login */}
              {authView === 'login' && (
                <LoginForm
                  onForgotPassword={() => setAuthView('forgot-password')}
                  onForgotUsername={() => setAuthView('forgot-username')}
                />
              )}

              {/* Register */}
              {authView === 'register' && <RegisterForm />}

              {/* Forgot Username */}
              {authView === 'forgot-username' && (
                <div className="space-y-5">
                  {forgotUsernameResult ? (
                    <div className="space-y-4 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                        <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="mx-auto max-w-xs text-sm text-gray-600 dark:text-gray-400">
                        {forgotUsernameResult}
                      </p>
                      <button onClick={backToLogin} className={primaryButtonClass}>
                        Back to Sign In
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotUsername} className="space-y-5">
                      {forgotUsernameError && (
                        <div className="rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                          {forgotUsernameError}
                        </div>
                      )}
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">Email Address</label>
                        <input
                          type="email"
                          value={forgotUsernameEmail}
                          onChange={(e) => { setForgotUsernameEmail(e.target.value); setForgotUsernameError(null); }}
                          placeholder="Enter your email address"
                          className={inputClass}
                          required
                        />
                      </div>
                      <button type="submit" disabled={forgotUsernameLoading} className={primaryButtonClass}>
                        {forgotUsernameLoading ? 'Looking up...' : 'Find My Username'}
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* Forgot Password */}
              {authView === 'forgot-password' && forgotPasswordSent && (
                <div className="space-y-4 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                    <svg className="h-8 w-8 text-primary-700 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2.5" />
                      <path d="M3 7l9 6 9-6" />
                    </svg>
                  </div>
                  <p className="mx-auto max-w-xs text-sm text-gray-600 dark:text-gray-400">{forgotPasswordSent}</p>
                  <p className="mx-auto max-w-xs text-xs text-gray-500 dark:text-gray-400">
                    The link is valid for 15 minutes. Check your spam folder, or contact your administrator if nothing arrives.
                  </p>
                  <button onClick={backToLogin} className={primaryButtonClass}>
                    Back to Sign In
                  </button>
                </div>
              )}

              {authView === 'forgot-password' && !forgotPasswordSent && (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  {forgotPasswordError && (
                    <div className="rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      {forgotPasswordError}
                    </div>
                  )}
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">Email Address</label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => { setForgotPasswordEmail(e.target.value); setForgotPasswordError(null); }}
                      placeholder="Enter your email address"
                      className={inputClass}
                      required
                    />
                  </div>
                  <button type="submit" disabled={forgotPasswordLoading} className={primaryButtonClass}>
                    {forgotPasswordLoading ? 'Sending...' : 'Reset Password'}
                  </button>
                </form>
              )}

              {/* Reset Password */}
              {authView === 'reset-password' && (
                <div className="space-y-5">
                  {resetSuccess ? (
                    <div className="space-y-4 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                        <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">Your password has been reset successfully.</p>
                      <button onClick={backToLogin} className={primaryButtonClass}>
                        Sign In with New Password
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleResetPassword} className="space-y-5">
                      {resetError && (
                        <div className="rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                          {resetError}
                        </div>
                      )}
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => { setNewPassword(e.target.value); setResetError(null); }}
                          placeholder="At least 6 characters"
                          className={inputClass}
                          minLength={6}
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">Confirm New Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); setResetError(null); }}
                          placeholder="Re-enter your new password"
                          className={inputClass}
                          minLength={6}
                          required
                        />
                      </div>
                      <button type="submit" disabled={resetLoading} className={primaryButtonClass}>
                        {resetLoading ? 'Resetting...' : 'Set New Password'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Footer links */}
            {authView === 'login' && (
              <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Don't have an account?{' '}
                <button
                  onClick={() => handleTabChange('register')}
                  className="font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                >
                  Sign up
                </button>
              </p>
            )}

            {authView === 'register' && (
              <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Already have an account?{' '}
                <button
                  onClick={() => handleTabChange('login')}
                  className="font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                >
                  Sign in
                </button>
              </p>
            )}

            {(authView === 'forgot-username' || authView === 'forgot-password') && (
              <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Remember your credentials?{' '}
                <button
                  onClick={backToLogin}
                  className="font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                >
                  Back to sign in
                </button>
              </p>
            )}
          </div>

          {/* Bottom info */}
          <p className="mt-8 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} classrooms
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
